import { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { tournamentApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'

/**
 * Read-only communication history between the reseller and the customer, shown
 * on the booking detail + custom-trip builder pages. One line per communication
 * — manual messages (reseller/customer threads) + automated emails — newest
 * first. Each line expands inline to view the content, with a "pop out" to a
 * full modal. Email bodies are sanitized + isolated in a sandboxed iframe.
 *
 * Pass EITHER bookingId OR quoteId; the backend returns the full lifecycle
 * (quote + booking) either way.
 */
export default function CommunicationHistory({ bookingId = null, quoteId = null }) {
  const { t } = useLanguage()
  const [items, setItems] = useState(null)
  const [err, setErr] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [modalItem, setModalItem] = useState(null)

  useEffect(() => {
    const url = bookingId
      ? `/portal/reseller/bookings/${bookingId}/communications`
      : quoteId ? `/portal/reseller/quotes/${quoteId}/communications` : null
    if (!url) return
    let cancelled = false
    tournamentApi.get(url)
      .then(r => { if (!cancelled) setItems(r.data?.communications || []) })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.message || e.message) })
    return () => { cancelled = true }
  }, [bookingId, quoteId])

  if (err) return <div style={{ ...h3 }}>{t('sp_comm_title', 'Communication history')}</div>

  return (
    <>
      <h3 style={h3}>{t('sp_comm_title', 'Communication history')}</h3>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {items == null ? (
          <div style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>{t('sp_loading', 'Loading…')}</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>{t('sp_comm_empty', 'No communications yet.')}</div>
        ) : items.map(c => {
          const open = openId === c.id
          return (
            <div key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div onClick={() => setOpenId(open ? null : c.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <ChannelBadge channel={c.channel} automated={c.automated} t={t} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.subject || '—'}</div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <DirectionText direction={c.direction} t={t} /> · {c.who || '—'}
                    {c.status && c.status !== 'sent' && c.status !== 'queued' && (
                      <span style={{ color: c.status === 'failed' || c.status === 'bounced' ? '#b91c1c' : '#b45309', fontWeight: 600 }}>
                        {' · '}{c.status === 'unsubscribed' ? t('sp_comm_status_unsub', 'Not sent (opted out)') : t('sp_comm_status_failed', 'Failed to send')}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtWhen(c.at)}</div>
                <button type="button" onClick={e => { e.stopPropagation(); setOpenId(open ? null : c.id) }}
                  style={linkBtn}>{open ? t('sp_comm_hide', 'Hide') : t('sp_comm_view', 'View')}</button>
              </div>
              {open && (
                <div style={{ padding: '0 14px 14px' }}>
                  <CommBody item={c} />
                  <div style={{ marginTop: 6 }}>
                    <button type="button" onClick={() => setModalItem(c)} style={linkBtn}>↗ {t('sp_comm_popout', 'Open in window')}</button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {modalItem && (
        <div onClick={() => setModalItem(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(760px, 100%)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{modalItem.subject || '—'}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  <DirectionText direction={modalItem.direction} t={t} /> · {modalItem.who || '—'} · {fmtWhen(modalItem.at)}
                </div>
              </div>
              <button type="button" onClick={() => setModalItem(null)}
                style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#475569', padding: '6px 12px', cursor: 'pointer' }}>
                {t('sp_comm_close', 'Close')}
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: modalItem.bodyIsHtml ? 0 : 18 }}>
              <CommBody item={modalItem} tall />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Email bodies are HTML → sanitize + isolate in a sandboxed iframe (no scripts,
// styles can't leak into the portal). Messages are plain text.
//
// sandbox grants ONLY the popup tokens — deliberately NOT allow-scripts /
// allow-same-origin, so the sanitized email still can't run code or reach the
// portal's origin. We need them because every link carries `target="_blank"`
// (via <base>): an empty sandbox has no allow-popups, so the browser silently
// blocks a normal left-click on those links — only Ctrl/middle-click (Chrome's
// native "open in new tab", which bypasses the sandbox) worked. That's the
// "Pay my share only opens if you hold Ctrl" report. allow-popups lets the
// click open the tab; allow-popups-to-escape-sandbox makes the opened page a
// normal (un-sandboxed) top-level load so the pay-share app runs.
function CommBody({ item, tall = false }) {
  if (item.bodyIsHtml) {
    const clean = DOMPurify.sanitize(String(item.body || ''), { USE_PROFILES: { html: true } })
    return (
      <iframe title="email" sandbox="allow-popups allow-popups-to-escape-sandbox"
        style={{ width: '100%', height: tall ? '70vh' : 360, border: tall ? 'none' : '1px solid #e2e8f0', borderRadius: tall ? 0 : 8, background: '#fff' }}
        srcDoc={`<!doctype html><meta charset="utf-8"><base target="_blank"><div style="font-family:system-ui,Segoe UI,Arial,sans-serif;font-size:14px;color:#0f172a;padding:12px">${clean}</div>`} />
    )
  }
  return (
    <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#334155', lineHeight: 1.5, border: tall ? 'none' : '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
      {item.body || '—'}
    </div>
  )
}

function ChannelBadge({ channel, automated, t }) {
  const isEmail = channel === 'email'
  const label = isEmail ? t('sp_comm_ch_email', 'Email') : t('sp_comm_ch_message', 'Message')
  const kind = automated ? t('sp_comm_automated', 'Automated') : t('sp_comm_manual', 'Manual')
  const palette = isEmail ? { bg: '#eff6ff', fg: '#1e40af' } : { bg: '#f0fdf4', fg: '#166534' }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: 78, flexShrink: 0 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: palette.bg, color: palette.fg }}>{label}</span>
      <span style={{ fontSize: 10, color: '#94a3b8' }}>{kind}</span>
    </div>
  )
}

function DirectionText({ direction, t }) {
  if (direction === 'from_customer') return <span style={{ color: '#6d28d9' }}>← {t('sp_comm_from_customer', 'From customer')}</span>
  if (direction === 'internal') return <span>{t('sp_comm_internal', 'Internal')}</span>
  return <span>→ {t('sp_comm_to_customer', 'To customer')}</span>
}

function fmtWhen(v) {
  if (!v) return '—'
  const d = new Date(v)
  return isNaN(d) ? '—' : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const h3 = { fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginTop: 24, marginBottom: 8 }
const linkBtn = { border: 'none', background: 'none', color: '#0f766e', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', flexShrink: 0, padding: 0 }
